import type { useCreateChat } from '@services/chat/mutations/useCreateChat'
import type { useUpdateChat } from '@services/chat/mutations/useUpdateChat'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChatDetail } from 'types/api'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function ChatModal(props: {
  chat?: ChatDetail
  user_id?: string
  createChat: ReturnType<typeof useCreateChat<'ADMIN'>>
  updateChat: ReturnType<typeof useUpdateChat>
}) {
  const { createChat, updateChat } = props
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState(props.chat?.title ?? '')
  const { t } = useTranslation('chat')

  useEffect(() => {
    if (open) setTitle(props.chat?.title ?? '')
  }, [open, props.chat])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (props.chat) updateChat.mutate({ id: props.chat.id, body: { title } })
    else createChat.mutate({ body: { title, user: props.user_id } })
    setOpen(false)
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        <Plus className="h-4 w-4" />
        {t('form.labels.new')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {props.chat
                ? t('form.common.edit_chat')
                : t('form.common.create_chat')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="mb-1.5 block">{t('form.labels.title')}</Label>
              <Input
                autoFocus
                placeholder={t('form.common.placeholder')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit">
                <Plus className="h-4 w-4" />
                {props.chat ? t('form.labels.edit') : t('form.labels.create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
